import {
  halfValues,
  initialPeriodsTableRow,
  IPeriodNames,
  IPeriodsTableRow,
  monthValues,
  PERIOD_NAMES,
  yearValues,
} from '../constants/periods';

const THRESHOLD = 1e-3;

const PERIOD_DIMS = [
  PERIOD_NAMES.MONTH_VALUES,
  PERIOD_NAMES.QUARTER_VALUES,
  PERIOD_NAMES.HALF_VALUES,
  PERIOD_NAMES.YEAR_VALUES,
] as const;

const formatValue = (value: number): string => (Math.abs(value) < THRESHOLD ? '0' : String(value));

const setPeriodValue = (row: IPeriodsTableRow, dim: (typeof PERIOD_DIMS)[number], key: string, value: string) => {
  (row[dim] as Record<string, string | number>)[key] = value;
};

const getPeriodValue = (row: IPeriodsTableRow, dim: (typeof PERIOD_DIMS)[number], key: string): number =>
  Number((row[dim] as Record<string, string | number>)[key]) || 0;

/** Sum of all period cells for the selected forecast dimension. */
export const sumPeriodTableRowValues = (row: IPeriodsTableRow, period: IPeriodNames): number =>
  Object.values(row[period] ?? {}).reduce<number>((sum, value) => sum + (Number(value) || 0), 0);

/** Sum of all 5 forecast years (matches Excel E20/E21 = SUM(X:AB) on Opening Balance Sheets). */
export const sumForecastYearsTableRowValues = (row: IPeriodsTableRow): number =>
  sumPeriodTableRowValues(row, PERIOD_NAMES.YEAR_VALUES);

/** Resolves a single forecast year total (Excel columns X–AB). Falls back to half/month aggregates. */
const getForecastYearTotal = (row: IPeriodsTableRow, year: 1 | 2 | 3 | 4 | 5): number => {
  const yearValue = getPeriodValue(row, PERIOD_NAMES.YEAR_VALUES, `y${year}`);

  if (yearValue > THRESHOLD) {
    return yearValue;
  }

  if (year === 1) {
    const halfSum =
      getPeriodValue(row, PERIOD_NAMES.HALF_VALUES, 'h1') + getPeriodValue(row, PERIOD_NAMES.HALF_VALUES, 'h2');
    if (halfSum > THRESHOLD) {
      return halfSum;
    }

    return sumPeriodTableRowValues(row, PERIOD_NAMES.MONTH_VALUES);
  }

  if (year === 2) {
    const halfSum =
      getPeriodValue(row, PERIOD_NAMES.HALF_VALUES, 'h3') + getPeriodValue(row, PERIOD_NAMES.HALF_VALUES, 'h4');
    if (halfSum > THRESHOLD) {
      return halfSum;
    }
  }

  if (year === 3) {
    const halfSum =
      getPeriodValue(row, PERIOD_NAMES.HALF_VALUES, 'h5') + getPeriodValue(row, PERIOD_NAMES.HALF_VALUES, 'h6');
    if (halfSum > THRESHOLD) {
      return halfSum;
    }
  }

  return 0;
};

/** Sum of 5 forecast years using year/half/month aggregates (Excel SUM(X:AB)). */
export const sumFiveYearForecastTableRowValues = (row: IPeriodsTableRow): number =>
  ([1, 2, 3, 4, 5] as const).reduce((sum, year) => sum + getForecastYearTotal(row, year), 0);

/** Long-term debt opening balance control limit (Excel E42): borrowed funds − loan repayments over 5 years. */
export const sumLongTermDebtOpeningBalanceControlLimit = (
  borrowedFunds: IPeriodsTableRow,
  repaymentOfLoan: IPeriodsTableRow,
): number => sumFiveYearForecastTableRowValues(borrowedFunds) - sumFiveYearForecastTableRowValues(repaymentOfLoan);

/** PPE opening balance control limit (Excel E21): depreciation + proceeds + gains/losses + grants over 5 years. */
export const sumPpeOpeningBalanceControlLimit = (
  depreciation: IPeriodsTableRow,
  proceedsFromSale: IPeriodsTableRow,
  gainsAndLossesFromAssetSales: IPeriodsTableRow,
  governmentGrants: IPeriodsTableRow,
): number =>
  sumForecastYearsTableRowValues(depreciation) +
  sumForecastYearsTableRowValues(proceedsFromSale) +
  sumForecastYearsTableRowValues(gainsAndLossesFromAssetSales) +
  sumForecastYearsTableRowValues(governmentGrants);

export const isOpeningBalanceAssetsEmpty = (
  propertyPlantAndEquipment: string | number,
  intangibleAssets: string | number,
): boolean =>
  (Number(propertyPlantAndEquipment) === 0 || propertyPlantAndEquipment === '') &&
  (Number(intangibleAssets) === 0 || intangibleAssets === '');

export const isHighlightedPeriodColumn = (columnName: string, highlightedColumns?: Set<string>): boolean => {
  if (!highlightedColumns?.size) return false;
  if (highlightedColumns.has(columnName)) return true;

  const periodKey = columnName.split('.').pop();
  return periodKey ? highlightedColumns.has(periodKey) : false;
};

export const expandPeriodKeysToAggregates = (monthKeys: Set<string>): Set<string> => {
  const excessivePeriodKeys = new Set(monthKeys);
  if (monthKeys.has('m1') || monthKeys.has('m2') || monthKeys.has('m3')) excessivePeriodKeys.add('q1');
  if (monthKeys.has('m4') || monthKeys.has('m5') || monthKeys.has('m6')) excessivePeriodKeys.add('q2');
  if (monthKeys.has('m7') || monthKeys.has('m8') || monthKeys.has('m9')) excessivePeriodKeys.add('q3');
  if (monthKeys.has('m10') || monthKeys.has('m11') || monthKeys.has('m12')) excessivePeriodKeys.add('q4');
  if (excessivePeriodKeys.has('q1') || excessivePeriodKeys.has('q2')) excessivePeriodKeys.add('h1');
  if (excessivePeriodKeys.has('q3') || excessivePeriodKeys.has('q4')) excessivePeriodKeys.add('h2');
  if (excessivePeriodKeys.has('h1') || excessivePeriodKeys.has('h2')) excessivePeriodKeys.add('y1');
  return excessivePeriodKeys;
};

/** Keeps previous period balance when a cumulative value would go negative. */
export const applyRunningBalanceFloor = (row: IPeriodsTableRow, initial: string | number): IPeriodsTableRow => {
  const result = structuredClone(row);
  let prev = Number(initial) || 0;

  for (let i = 1; i <= Object.keys(monthValues).length; i++) {
    const key = `m${i}`;
    const val = getPeriodValue(result, PERIOD_NAMES.MONTH_VALUES, key);
    const floored = val < 0 ? prev : val;
    setPeriodValue(result, PERIOD_NAMES.MONTH_VALUES, key, formatValue(floored));
    prev = getPeriodValue(result, PERIOD_NAMES.MONTH_VALUES, key);
  }

  result.quarterValues.q1 = result.monthValues.m3;
  result.quarterValues.q2 = result.monthValues.m6;
  result.quarterValues.q3 = result.monthValues.m9;
  result.quarterValues.q4 = result.monthValues.m12;
  result.halfValues.h1 = result.monthValues.m6;
  result.halfValues.h2 = result.monthValues.m12;
  result.yearsValues.y1 = result.halfValues.h2;

  for (let i = 3; i <= Object.keys(halfValues).length; i++) {
    const key = `h${i}`;
    const val = getPeriodValue(result, PERIOD_NAMES.HALF_VALUES, key);
    const floored = val < 0 ? prev : val;
    setPeriodValue(result, PERIOD_NAMES.HALF_VALUES, key, formatValue(floored));
    prev = getPeriodValue(result, PERIOD_NAMES.HALF_VALUES, key);
  }

  for (let i = 2; i <= Object.keys(yearValues).length; i++) {
    const key = `y${i}`;
    const val = getPeriodValue(result, PERIOD_NAMES.YEAR_VALUES, key);
    const floored = val < 0 ? prev : val;
    setPeriodValue(result, PERIOD_NAMES.YEAR_VALUES, key, formatValue(floored));
    prev = getPeriodValue(result, PERIOD_NAMES.YEAR_VALUES, key);
  }

  return result;
};

/** End-of-period balances (balance sheet). */
export const syncPeriodAggregatesFromMonths = (row: IPeriodsTableRow): IPeriodsTableRow => {
  const result = structuredClone(row);
  result.quarterValues.q1 = result.monthValues.m3;
  result.quarterValues.q2 = result.monthValues.m6;
  result.quarterValues.q3 = result.monthValues.m9;
  result.quarterValues.q4 = result.monthValues.m12;
  result.halfValues.h1 = result.monthValues.m6;
  result.halfValues.h2 = result.monthValues.m12;
  result.yearsValues.y1 = result.halfValues.h2;
  return result;
};

/** Cash-flow proceeds: quarter/half/year are sums of months (matches reCalculatedPeriodValues). */
export const syncProceedsAggregatesFromMonths = (row: IPeriodsTableRow): IPeriodsTableRow => {
  const result = structuredClone(row);
  const sumMonths = (keys: (keyof typeof monthValues)[]) =>
    keys.reduce((sum, key) => sum + (Number(result.monthValues[key]) || 0), 0);

  result.quarterValues.q1 = String(sumMonths(['m1', 'm2', 'm3']));
  result.quarterValues.q2 = String(sumMonths(['m4', 'm5', 'm6']));
  result.quarterValues.q3 = String(sumMonths(['m7', 'm8', 'm9']));
  result.quarterValues.q4 = String(sumMonths(['m10', 'm11', 'm12']));
  result.halfValues.h1 = String(Number(result.quarterValues.q1) + Number(result.quarterValues.q2));
  result.halfValues.h2 = String(Number(result.quarterValues.q3) + Number(result.quarterValues.q4));
  result.yearsValues.y1 = String(Number(result.halfValues.h1) + Number(result.halfValues.h2));
  return result;
};

/** Caps proceeds from sale using month-level available PPE balance only. */
export const capProceedsByAvailableBalance = (
  rawInput: IPeriodsTableRow,
  availableBalance: IPeriodsTableRow,
): { effective: IPeriodsTableRow; excessivePeriodKeys: Set<string> } => {
  const effective = structuredClone(initialPeriodsTableRow);
  const excessiveMonthKeys = new Set<string>();

  Object.keys(monthValues).forEach((key) => {
    const raw = Math.max(0, getPeriodValue(rawInput, PERIOD_NAMES.MONTH_VALUES, key));
    const available = Math.max(0, getPeriodValue(availableBalance, PERIOD_NAMES.MONTH_VALUES, key));
    const capped = Math.min(raw, available);
    setPeriodValue(effective, PERIOD_NAMES.MONTH_VALUES, key, formatValue(capped));
    if (raw > available + THRESHOLD) {
      excessiveMonthKeys.add(key);
    }
  });

  return {
    effective: syncPeriodAggregatesFromMonths(effective),
    excessivePeriodKeys: expandPeriodKeysToAggregates(excessiveMonthKeys),
  };
};

/** Zeros excessive proceeds periods for total/PDF using the same keys as UI warnings. */
export const applyExcessiveProceedsForTotal = (
  rawProceeds: IPeriodsTableRow,
  excessivePeriodKeys: Set<string>,
): IPeriodsTableRow => {
  if (!excessivePeriodKeys.size) return rawProceeds;

  const result = structuredClone(rawProceeds);
  Object.keys(monthValues).forEach((key) => {
    if (excessivePeriodKeys.has(key)) {
      setPeriodValue(result, PERIOD_NAMES.MONTH_VALUES, key, '0');
    }
  });

  return syncProceedsAggregatesFromMonths(result);
};

export const getOpeningPpeBalanceForProceeds = (openingPpe: string | number): IPeriodsTableRow => {
  const openingPpeNum = Number(openingPpe) || 0;
  if (openingPpeNum <= 0) return initialPeriodsTableRow;

  const result = structuredClone(initialPeriodsTableRow);
  Object.keys(monthValues).forEach((key) => {
    setPeriodValue(result, PERIOD_NAMES.MONTH_VALUES, key, String(openingPpeNum));
  });

  return result;
};

export const zeroExcessivePeriodValues = (
  row: IPeriodsTableRow,
  excessivePeriodKeys: Set<string>,
): IPeriodsTableRow => {
  if (!excessivePeriodKeys.size) return row;

  const result = structuredClone(row);

  PERIOD_DIMS.forEach((dim) => {
    Object.keys(result[dim]).forEach((key) => {
      if (excessivePeriodKeys.has(key)) {
        setPeriodValue(result, dim, key, '0');
      }
    });
  });

  return result;
};

/** Caps raw repayment (positive values) against running long-term debt balance. */
export const capRepaymentByDebtBalance = (
  rawRepayment: IPeriodsTableRow,
  initialDebt: string | number,
  borrowedFunds: IPeriodsTableRow,
): {
  effectiveRepayment: IPeriodsTableRow;
  effectiveRepaymentDebt: IPeriodsTableRow;
  excessivePeriodKeys: Set<string>;
} => {
  const effectiveRepayment = structuredClone(initialPeriodsTableRow);
  const excessiveMonthKeys = new Set<string>();
  let balance = Number(initialDebt) || 0;

  Object.keys(monthValues).forEach((key) => {
    balance += Math.max(0, getPeriodValue(borrowedFunds, PERIOD_NAMES.MONTH_VALUES, key));

    const raw = Math.max(0, getPeriodValue(rawRepayment, PERIOD_NAMES.MONTH_VALUES, key));
    const capped = Math.min(raw, balance);
    setPeriodValue(effectiveRepayment, PERIOD_NAMES.MONTH_VALUES, key, formatValue(capped));

    if (raw > balance + THRESHOLD) {
      excessiveMonthKeys.add(key);
    }

    balance = Math.max(0, balance - capped);
  });

  effectiveRepayment.quarterValues.q1 = effectiveRepayment.monthValues.m3;
  effectiveRepayment.quarterValues.q2 = effectiveRepayment.monthValues.m6;
  effectiveRepayment.quarterValues.q3 = effectiveRepayment.monthValues.m9;
  effectiveRepayment.quarterValues.q4 = effectiveRepayment.monthValues.m12;
  effectiveRepayment.halfValues.h1 = effectiveRepayment.monthValues.m6;
  effectiveRepayment.halfValues.h2 = effectiveRepayment.monthValues.m12;
  effectiveRepayment.yearsValues.y1 = effectiveRepayment.halfValues.h2;

  for (let i = 3; i <= Object.keys(halfValues).length; i++) {
    const key = `h${i}`;
    const prevKey = `h${i - 1}`;
    const raw = Math.max(0, getPeriodValue(rawRepayment, PERIOD_NAMES.HALF_VALUES, key));
    const prevBalance = getPeriodValue(effectiveRepayment, PERIOD_NAMES.HALF_VALUES, prevKey);
    const capped = Math.min(raw, prevBalance);
    setPeriodValue(effectiveRepayment, PERIOD_NAMES.HALF_VALUES, key, formatValue(capped));
    if (raw > prevBalance + THRESHOLD) excessiveMonthKeys.add(key);
  }

  for (let i = 2; i <= Object.keys(yearValues).length; i++) {
    const key = `y${i}`;
    const prevKey = `y${i - 1}`;
    const raw = Math.max(0, getPeriodValue(rawRepayment, PERIOD_NAMES.YEAR_VALUES, key));
    const prevBalance = getPeriodValue(effectiveRepayment, PERIOD_NAMES.YEAR_VALUES, prevKey);
    const capped = Math.min(raw, prevBalance);
    setPeriodValue(effectiveRepayment, PERIOD_NAMES.YEAR_VALUES, key, formatValue(capped));
    if (raw > prevBalance + THRESHOLD) excessiveMonthKeys.add(key);
  }

  const effectiveRepaymentDebt = structuredClone(initialPeriodsTableRow);
  PERIOD_DIMS.forEach((dim) => {
    Object.entries(effectiveRepayment[dim]).forEach(([key, val]) => {
      setPeriodValue(effectiveRepaymentDebt, dim, key, formatValue(-Number(val)));
    });
  });

  return {
    effectiveRepayment,
    effectiveRepaymentDebt,
    excessivePeriodKeys: expandPeriodKeysToAggregates(excessiveMonthKeys),
  };
};

export const getExcessiveInputPeriodKeys = (raw: IPeriodsTableRow, effective: IPeriodsTableRow): Set<string> => {
  const excessiveMonthKeys = new Set<string>();

  PERIOD_DIMS.forEach((dim) => {
    Object.entries(raw[dim]).forEach(([key, rawVal]) => {
      const rawNum = Number(rawVal) || 0;
      const effectiveNum = getPeriodValue(effective, dim, key);
      if (Math.abs(rawNum - effectiveNum) > THRESHOLD && rawNum > effectiveNum) {
        excessiveMonthKeys.add(key);
      }
    });
  });

  return expandPeriodKeysToAggregates(excessiveMonthKeys);
};

/**
 * Excel 10.5 rows 9/20: IF(controlParam > 0, rawValue, 0).
 * Month-level conditionals are summed into Q/H1/Y1; h3+ and y2+ use direct IF at that level.
 */
const computeConditionalByControlParam = (
  controlParam: IPeriodsTableRow,
  rawValues: IPeriodsTableRow,
): { conditional: IPeriodsTableRow; excessivePeriodKeys: Set<string> } => {
  const conditional = structuredClone(initialPeriodsTableRow);
  const excessiveMonthKeys = new Set<string>();
  const excessiveAggregateKeys = new Set<string>();

  const applyIf = (dim: (typeof PERIOD_DIMS)[number], keys: string[], aggregateKeys: Set<string>) => {
    keys.forEach((key) => {
      const raw = Math.max(0, getPeriodValue(rawValues, dim, key));
      const balance = getPeriodValue(controlParam, dim, key);
      const effective = balance > THRESHOLD ? raw : 0;
      setPeriodValue(conditional, dim, key, formatValue(effective));

      if (raw > THRESHOLD && effective === 0) {
        aggregateKeys.add(key);
      }
    });
  };

  applyIf(PERIOD_NAMES.MONTH_VALUES, Object.keys(monthValues), excessiveMonthKeys);

  const syncedFromMonths = syncProceedsAggregatesFromMonths(conditional);
  conditional.quarterValues = syncedFromMonths.quarterValues;
  conditional.halfValues.h1 = syncedFromMonths.halfValues.h1;
  conditional.halfValues.h2 = syncedFromMonths.halfValues.h2;
  conditional.yearsValues.y1 = syncedFromMonths.yearsValues.y1;

  for (let i = 3; i <= Object.keys(halfValues).length; i++) {
    applyIf(PERIOD_NAMES.HALF_VALUES, [`h${i}`], excessiveAggregateKeys);
  }

  for (let i = 2; i <= Object.keys(yearValues).length; i++) {
    applyIf(PERIOD_NAMES.YEAR_VALUES, [`y${i}`], excessiveAggregateKeys);
  }

  return {
    conditional,
    excessivePeriodKeys: new Set([
      ...Array.from(expandPeriodKeysToAggregates(excessiveMonthKeys)),
      ...Array.from(excessiveAggregateKeys),
    ]),
  };
};

/** Excel 10.5 row 9 → 10.6 Proceeds from issuance of debt. */
export const computeConditionalBorrowed = (
  controlParam: IPeriodsTableRow,
  borrowedFunds: IPeriodsTableRow,
): { conditionalBorrowed: IPeriodsTableRow; excessivePeriodKeys: Set<string> } => {
  const { conditional, excessivePeriodKeys } = computeConditionalByControlParam(controlParam, borrowedFunds);
  return { conditionalBorrowed: conditional, excessivePeriodKeys };
};

/** Excel 10.5 row 20 → 10.6 Repayment of debt (stored as negative cash outflow). */
export const computeConditionalRepayment = (
  controlParam: IPeriodsTableRow,
  rawRepayment: IPeriodsTableRow,
): {
  conditionalRepayment: IPeriodsTableRow;
  conditionalRepaymentDebt: IPeriodsTableRow;
  excessivePeriodKeys: Set<string>;
} => {
  const { conditional, excessivePeriodKeys } = computeConditionalByControlParam(controlParam, rawRepayment);
  const conditionalRepaymentDebt = structuredClone(initialPeriodsTableRow);

  PERIOD_DIMS.forEach((dim) => {
    Object.entries(conditional[dim]).forEach(([key, val]) => {
      setPeriodValue(conditionalRepaymentDebt, dim, key, formatValue(-Number(val)));
    });
  });

  return { conditionalRepayment: conditional, conditionalRepaymentDebt, excessivePeriodKeys };
};
